import { useEffect, useRef, useState } from 'react'

import ThanxAIAutoScrollContainer from 'components/AI/ThanxAIAutoScrollContainer'
import CreateAnotherSegmentCTA from 'components/AI/ThanxAIConversation/CreateAnotherSegmentCTA'
import ThanxAIHelpLinks from 'components/AI/ThanxAIHelpLinks'
import ThanxAIMessageList, {
  hasAnySavedSegmentInMessages,
} from 'components/AI/ThanxAIMessageList'
import ThanxAIPromptInput from 'components/AI/ThanxAIPromptInput'
import { useAIChat } from 'contexts/AIChatConnectionProvider'
import Formsy from 'formsy-react'
import { buildTranslate } from 'locales'
import type { Message } from 'slices/aiConversations/types'
import './ThanxAIConversation.scss'

const t = buildTranslate('components.ai.conversation')
const tSegments = buildTranslate('segments.ai_builder.empty_state')

export interface ThanxAIConversationProps {
  onMessageSubmit?: (message: string) => void
  placeholder?: string
  onCreateAnother: () => void
}

const ThanxAIConversation = ({
  onMessageSubmit,
  placeholder,
  onCreateAnother,
}: ThanxAIConversationProps) => {
  // Get WebSocket data and functionality from provider
  const {
    isConnected,
    sendMessage,
    messages,
    currentStreamingMessage,
    awaitingResponse,
  } = useAIChat()

  // Use internal message handler if none provided
  const handleMessageSubmit =
    onMessageSubmit ||
    ((messageContent: string) => {
      if (isConnected && messageContent.trim()) {
        sendMessage(messageContent)
      }
    })

  // Use provided placeholder or default
  const displayPlaceholder = placeholder || t('reply_placeholder')
  const formRef = useRef<InstanceType<typeof Formsy> | null>(null)
  const inputContainerRef = useRef<HTMLDivElement | null>(null)
  const [inputContainerHeight, setInputContainerHeight] = useState<number>(144)
  const isStreaming = Boolean(currentStreamingMessage)

  // Check if any segment has been saved to disable input
  const isSegmentSaved = hasAnySavedSegmentInMessages(messages)

  const handleSubmit = (model: { message: string }) => {
    handleMessageSubmit(model.message)
    // Clear the form after submission
    if (formRef.current) {
      formRef.current.reset()
    }
  }

  // Track input container height changes with ResizeObserver
  useEffect(() => {
    const inputContainer = inputContainerRef.current
    if (!inputContainer) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setInputContainerHeight(entry.contentRect.height)
      }
    })

    resizeObserver.observe(inputContainer)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const helpLinkTextAction = {
    url: 'https://thanxwiki.notion.site/43616f27eefa4fda85a1b18e4c619dfe?pvs=105',
    text: tSegments('help_link_action_text'),
  }

  return (
    <div
      className="thanx-ai-conversation"
      role="region"
      aria-label={t('conversation_label')}
    >
      <ThanxAIAutoScrollContainer>
        <ThanxAIMessageList
          bottomSpacing={inputContainerHeight}
          onMessageSubmit={handleMessageSubmit}
        />
      </ThanxAIAutoScrollContainer>
      <div
        className="thanx-ai-conversation__input-container"
        ref={inputContainerRef}
      >
        {isSegmentSaved ? (
          <CreateAnotherSegmentCTA onClick={onCreateAnother} />
        ) : (
          <Formsy ref={formRef} onSubmit={handleSubmit}>
            <ThanxAIPromptInput
              name="message"
              placeholder={displayPlaceholder}
              submitText={t('send_button_text')}
              isStreaming={isStreaming}
              awaitingResponse={awaitingResponse}
            />
          </Formsy>
        )}
        <ThanxAIHelpLinks
          helpLinkText={tSegments('help_link_text')}
          helpLinkTextAction={helpLinkTextAction}
        />
      </div>
    </div>
  )
}

export default ThanxAIConversation
export type { Message }
